import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { MdEvent, MdLocationOn } from 'react-icons/md';

const EventsPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('latest'); // upcoming, latest, previous

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        setLoading(true);
        const { data } = await api.get(`/events?timeFilter=${filter}`);
        setEvents(data.data || []);
      } catch (error) {
        console.error('Failed to fetch events', error);
      } finally {
        setLoading(false);
      }
    };
    fetchEvents();
  }, [filter]);

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-2xl md:text-4xl font-bold text-gray-900 mb-4">Our Events</h1>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Stay updated with our latest workshops, medical camps, and community outreach programs.
          </p>
        </div>

        <div className="flex flex-wrap justify-center mb-8 gap-3 md:gap-4">
          {['upcoming', 'latest', 'previous'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 md:px-6 py-2 rounded-full font-medium transition-colors ${filter === f
                  ? 'bg-red-600 text-white shadow-md'
                  : 'bg-white text-gray-600 border hover:bg-gray-50'
                }`}
            >
              {f.charAt(0).toUpperCase() + f.slice(1)} Events
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl shadow-sm border border-gray-100">
            <MdEvent className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-xl font-medium text-gray-900 mb-2">No {filter} events found</h3>
            <p className="text-gray-500">Check back later for more updates.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {events.map((event) => (
              <div key={event._id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
                <div className="aspect-w-16 aspect-h-9 w-full relative">
                  <img
                    src={event.image || 'https://via.placeholder.com/800x450'}
                    alt={event.title}
                    className="w-full h-48 object-contain bg-gray-50"
                  />
                  <div className="absolute top-4 right-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold shadow-sm ${event.status === 'upcoming' ? 'bg-green-100 text-green-800' :
                        event.status === 'ongoing' ? 'bg-blue-100 text-blue-800' :
                          event.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-gray-100 text-gray-800'
                      }`}>
                      {event.status.toUpperCase()}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">
                    {event.title}
                  </h3>

                  <div className="flex items-center text-sm text-gray-500 mb-4 space-x-4">
                    <div className="flex items-center">
                      <MdEvent className="mr-1.5 h-4 w-4" />
                      {new Date(event.date).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </div>
                    {event.location && (
                      <div className="flex items-center">
                        <MdLocationOn className="mr-1.5 h-4 w-4" />
                        {event.location}
                      </div>
                    )}
                  </div>

                  <p className="text-gray-600 line-clamp-3 mb-6">
                    {event.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;
